import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { useTheme } from "../context/ThemeContext";
import { db } from "../firebase/config";
import {
  collection,
  query,
  where,
  getDocs,
  deleteDoc,
  doc,
  writeBatch
} from "firebase/firestore";
import { getAuth } from "firebase/auth";

export default function Header() {
  const { currentUser, userProfile, logout } = useAuth();
  const theme = useTheme();
  const isChild = userProfile?.role === "child";
  const [menuOpen, setMenuOpen] = useState(false);
  const navigate = useNavigate();

  const initials = userProfile?.name
    ? userProfile.name.split(" ").map((n) => n[0]).join("").toUpperCase()
    : "?";

  async function handleLogout() {
    await logout();
    navigate("/login");
  }

  async function deleteParentAccount() {
    if (!window.confirm("Are you sure you want to delete your account? All your children's accounts and all reading data will be permanently deleted. This cannot be undone.")) return;
    try {
      const batch = writeBatch(db);

      // Get all children
      const childrenQuery = query(collection(db, "users"), where("parentId", "==", currentUser.uid));
      const childrenSnapshot = await getDocs(childrenQuery);

      // Delete each child's books and user document
      for (const childDoc of childrenSnapshot.docs) {
        const childBooksQuery = query(collection(db, "books"), where("userId", "==", childDoc.id));
        const childBooksSnapshot = await getDocs(childBooksQuery);
        childBooksSnapshot.docs.forEach((bookDoc) => batch.delete(bookDoc.ref));
        batch.delete(childDoc.ref);
      }

      // Delete parent's own books
      const parentBooksQuery = query(collection(db, "books"), where("userId", "==", currentUser.uid));
      const parentBooksSnapshot = await getDocs(parentBooksQuery);
      parentBooksSnapshot.docs.forEach((bookDoc) => batch.delete(bookDoc.ref));

      // Delete parent's user document
      batch.delete(doc(db, "users", currentUser.uid));

      await batch.commit();

      // Delete parent's auth account
      const auth = getAuth();
      await auth.currentUser.delete();

      navigate("/login");
    } catch (err) {
      if (err.code === "auth/requires-recent-login") {
        alert("For security, please sign out and sign back in before deleting your account.");
      } else {
        alert("Failed to delete account. Try again.");
      }
    }
  }

const styles = {
    header: {
      backgroundColor: isChild ? theme.bgCard : "white",
      padding: "12px 24px",
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center",
      boxShadow: isChild
        ? `0 2px 8px rgba(0,0,0,0.3)`
        : "0 2px 4px rgba(0,0,0,0.1)",
      borderBottom: isChild ? `2px solid ${theme.bgCardBorder}` : "none",
      position: "relative",
      zIndex: 100,
    },
    logo: { display: "flex", alignItems: "center", gap: 8, cursor: "pointer" },
    logoText: {
      fontSize: 18,
      fontWeight: "bold",
      color: isChild ? theme.colorAccent : "#2d3748",
      fontFamily: isChild ? theme.fontDisplay : "inherit",
      lineHeight: 1.6,
    },
    headerRight: { position: "relative" },
    avatarWrapper: {
      display: "flex",
      alignItems: "center",
      gap: 10,
      cursor: "pointer",
      padding: "6px 12px",
      borderRadius: 8,
      backgroundColor: isChild ? theme.bgPage : "#f7fafc",
    },
    avatar: {
      width: 36,
      height: 36,
      borderRadius: 18,
      backgroundColor: isChild ? theme.colorPrimary : "#4299e1",
      color: "white",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      fontSize: 14,
      fontWeight: "bold",
    },
    avatarName: {
      fontSize: 14,
      color: isChild ? theme.colorText : "#2d3748",
      fontWeight: "500",
      fontFamily: isChild ? theme.fontBody : "inherit",
    },
    hamburger: { fontSize: 18, color: isChild ? theme.colorTextSoft : "#718096" },
    dropdown: {
      position: "absolute",
      top: 56,
      right: 24,
      backgroundColor: isChild ? theme.bgCard : "white",
      borderRadius: 12,
      boxShadow: "0 8px 24px rgba(0,0,0,0.15)",
      minWidth: 220,
      zIndex: 200,
      overflow: "hidden",
      border: isChild ? `1px solid ${theme.bgCardBorder}` : "none",
    },
    dropdownHeader: {
      padding: "16px 20px",
      backgroundColor: isChild ? theme.bgPage : "#f7fafc",
    },
    dropdownName: {
      fontWeight: "bold",
      color: isChild ? theme.colorText : "#2d3748",
      margin: "0 0 4px 0",
      fontSize: 15,
      fontFamily: isChild ? theme.fontBody : "inherit",
    },
    dropdownEmail: {
      color: isChild ? theme.colorTextSoft : "#718096",
      margin: "0 0 4px 0",
      fontSize: 13,
    },
    dropdownRole: {
      color: isChild ? theme.colorAccent : "#805ad5",
      margin: 0,
      fontSize: 12,
    },
    dropdownDivider: {
      height: 1,
      backgroundColor: isChild ? theme.bgCardBorder : "#e2e8f0",
    },
    dropdownItem: {
      padding: "14px 20px",
      cursor: "pointer",
      fontSize: 14,
      color: isChild ? theme.colorText : "#2d3748",
      fontWeight: "500",
      fontFamily: isChild ? theme.fontBody : "inherit",
    },
  };

  return (
    <div style={styles.header} onClick={() => setMenuOpen(false)}>
      <div style={styles.logo} onClick={() => navigate("/")}>
        📚 <span style={styles.logoText}>Clarke Kids!</span>
      </div>

      <div style={styles.headerRight}>
        <div
          style={styles.avatarWrapper}
          onClick={(e) => { e.stopPropagation(); setMenuOpen(!menuOpen); }}
        >
          <div style={styles.avatar}>{initials}</div>
          <span style={styles.avatarName}>{userProfile?.name || "Loading..."}</span>
          <span style={styles.hamburger}>☰</span>
        </div>
      </div>

      {menuOpen && (
        <div style={styles.dropdown} onClick={(e) => e.stopPropagation()}>
          <div style={styles.dropdownHeader}>
            <p style={styles.dropdownName}>{userProfile?.name}</p>
            <p style={styles.dropdownEmail}>{currentUser?.email}</p>
            <p style={styles.dropdownRole}>
              {userProfile?.role === "parent" ? "👨‍👩‍👧 Parent" : "👦 Student"}
            </p>
          </div>
          <div style={styles.dropdownDivider} />
          <div style={styles.dropdownItem} onClick={() => { navigate("/"); setMenuOpen(false); }}>
            📚 My Books
          </div>
          {userProfile?.role === "parent" && (
            <div style={styles.dropdownItem} onClick={() => { navigate("/parent"); setMenuOpen(false); }}>
              👨‍👩‍👧 My Children
            </div>
          )}
          {userProfile?.role === "parent" && (
            <div style={styles.dropdownItem} onClick={() => { navigate("/settings"); setMenuOpen(false); }}>
              ⚙️ Account Settings
            </div>
          )}
          <div style={styles.dropdownDivider} />
          {userProfile?.role === "parent" && (
            <div style={{ ...styles.dropdownItem, color: "#c53030" }} onClick={deleteParentAccount}>
              🗑️ Delete My Account
            </div>
          )}
          <div style={{ ...styles.dropdownItem, color: "#f56565" }} onClick={handleLogout}>
            🚪 Sign Out
          </div>
        </div>
      )}
    </div>
  );
}